/** @jsxImportSource @emotion/react */
import React, { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import * as s from './style';
import AttributeTable from './AttributeTable';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import Cluster from 'ol/source/Cluster';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { Style, Icon, Circle as CircleStyle, Fill, Stroke } from 'ol/style';
import useBaseOlMap from 'hooks/useBaseOlMap';
import useDynamicVectorLayers from 'hooks/map/useDynamicVectorLayers';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { createOsmBaseLayer } from 'utils/map/baseLayers';
import { createEmpty, extend as extendExtent } from 'ol/extent';
import { getLayerStyle } from '../getLayerStyle';
import pinIcon from 'assets/icons/map_pin_red.svg';

const MapArea = ({
    layers,
    selectedLayerIndex,
    attrGeojson,
    isTableExpanded,
    handleTableExpand,
    focusPoint,
    attributeExpandSignal = 0,
    attributeTitle,
    attributeContent = null,
    disableAutoFit = false,
    fitExtent = null,
    onFitExtentConsumed = null,
    isFileDeletedAfterUpload
}) => {
    const [isTableCollapsed, setIsTableCollapsed] = useState(true);
    const [tableHeight, setTableHeight] = useState(380);
    const draggingRef = useRef(false);
    const startHeightRef = useRef(tableHeight);
    const startYRef = useRef(0);
    const osmBaseLayer = useMemo(() => createOsmBaseLayer({ title: 'OSM Base Map' }), []);
    const { mapRef, initializeMap } = useBaseOlMap({
        targetId: 'user_data_map_area',
        baseLayerFactory: () => osmBaseLayer,
        minZoom: 0,
        maxZoom: 28,
    });
    const { syncLayers } = useDynamicVectorLayers({ mapRef });
    const markerLayerRef = useRef(null);
    const markerSourceRef = useRef(null);
    const historyLayerRef = useRef(null);
    const historySourceRef = useRef(null);

    useEffect(() => {
        if (typeof isTableExpanded === 'boolean') {
            setIsTableCollapsed(!isTableExpanded);
        }
    }, [isTableExpanded]);

    useEffect(() => {
        if (attributeExpandSignal > 0) {
            setIsTableCollapsed(false);
        }
    }, [attributeExpandSignal]);

    useEffect(() => {
        initializeMap();
    }, [initializeMap]);

    // useEffect(() => {
    //     const map = mapRef.current;
    //     if (!map) return;

    //     const handlePointerMove = (event) => {
    //         // const coordinate = map.getEventCoordinate(event.originalEvent ?? event);
    //         const coordinate = map.getView().getCenter();
    //         if (!coordinate) return;
    //         console.log(`Map pointer move: x=${coordinate[0]}, y=${coordinate[1]}`);
    //     };

    //     map.on('pointermove', handlePointerMove);
    //     return () => {
    //         map.un('pointermove', handlePointerMove);
    //     };
    // }, [mapRef]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        if (markerLayerRef.current) return;

        const source = new VectorSource();
        const iconStyle = new Style({
            image: new Icon({
                src: pinIcon,
                anchor: [0.5, 1],
                anchorXUnits: 'fraction',
                anchorYUnits: 'fraction',
                scale: 0.85,
            }),
        });
        const layer = new VectorLayer({
            source,
            zIndex: 9999,
            style: () => iconStyle,
        });
        map.addLayer(layer);
        markerLayerRef.current = layer;
        markerSourceRef.current = source;

        return () => {
            map.removeLayer(layer);
            markerLayerRef.current = null;
            markerSourceRef.current = null;
        };
    }, [mapRef]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map || historyLayerRef.current) return;

        const source = new VectorSource();
        const layer = new VectorLayer({
            source,
            zIndex: 1000,
            visible: false,
            style: new Style({
                image: new CircleStyle({
                    radius: 4,
                    fill: new Fill({ color: 'rgba(37, 99, 235, 0.6)' }),
                    stroke: new Stroke({ color: 'rgba(15, 76, 217, 0.9)', width: 1 }),
                }),
            }),
        });
        map.addLayer(layer);
        historyLayerRef.current = layer;
        historySourceRef.current = source;

        return () => {
            map.removeLayer(layer);
            historyLayerRef.current = null;
            historySourceRef.current = null;
        };
    }, [mapRef]);

    const layerDescriptors = useMemo(
        () =>
            layers
                .map((layer, index) => {
                    if (!layer?.geojson) return null;

                    return {
                        id: `user-layer-${index}`,
                        visible: layer.visible,
                        createLayer: (mapInstance) => {
                            const features = new GeoJSON().readFeatures(layer.geojson, {
                                dataProjection: 'EPSG:3857',
                                featureProjection: mapInstance.getView().getProjection(),
                            });
                            if (!features.length) return null;

                            const geomType = features[0].getGeometry().getType().toLowerCase();
                            let source = new VectorSource({ features });
                            const clusterDistance = layer.clusterDistance ?? 10;
                            if (geomType.includes('point') && clusterDistance > 0) {
                                source = new Cluster({
                                    distance: clusterDistance,
                                    source,
                                });
                            }

                            return new VectorLayer({
                                source,
                                visible: layer.visible,
                                style: (feature) => getLayerStyle(layer, feature),
                            });
                        },
                    };
                })
                .filter(Boolean),
        [layers]
    );

    useEffect(() => {
        syncLayers(layerDescriptors);
    }, [layerDescriptors, syncLayers]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        const view = map.getView();
        if (!view) return;

        const overallExtent = createEmpty();
        let hasExtent = false;
        const projection = view.getProjection();

        layers.forEach((layer) => {
            if (!layer?.geojson) return;
            const features = new GeoJSON().readFeatures(layer.geojson, {
                dataProjection: 'EPSG:3857',
                featureProjection: projection,
            });
            features.forEach((feature) => {
                const geometry = feature.getGeometry();
                if (!geometry) return;
                extendExtent(overallExtent, geometry.getExtent());
                hasExtent = true;
            });
        });

        if (hasExtent && !disableAutoFit) {
            view.fit(overallExtent, {
                padding: [40, 40, 40, 40],
                duration: 500,
            });
        }
    }, [layers, mapRef, disableAutoFit]);

    useEffect(() => {
        if (!focusPoint || !mapRef.current) return;
        const view = mapRef.current.getView();
        if (!view) return;
        view.animate({
            center: focusPoint,
            duration: 400,
            zoom: 12,
        });
    }, [focusPoint, mapRef]);

    useEffect(() => {
        if (!fitExtent || !mapRef.current) return;
        const view = mapRef.current.getView();
        if (!view) return;
        view.fit(fitExtent, {
            padding: [40, 40, 40, 40],
            duration: 500,
        });
        onFitExtentConsumed?.();
    }, [fitExtent, mapRef, onFitExtentConsumed]);

    useEffect(() => {
        const source = markerSourceRef.current;
        if (!source) return;
        source.clear();
        if (!focusPoint) return;
        const feature = new Feature({
            geometry: new Point(focusPoint),
        });
        source.addFeature(feature);
    }, [focusPoint]);

    useEffect(() => {
        const source = historySourceRef.current;
        const map = mapRef.current;
        if (!source || !map) return;
        source.clear();
        if (!attrGeojson) return;
        const format = new GeoJSON();
        const projection = map.getView()?.getProjection();
        try {
            const features = format.readFeatures(attrGeojson, {
                dataProjection: 'EPSG:3857',
                featureProjection: projection,
            });
            source.addFeatures(features);
        } catch (e) {
            console.error('Failed to load history geojson', e);
        }
    }, [attrGeojson]);

    const startResize = (event) => {
        event.preventDefault();
        draggingRef.current = true;
        startYRef.current = event.clientY;
        startHeightRef.current = tableHeight;
        window.addEventListener('mousemove', handleResize);
        window.addEventListener('mouseup', stopResize);
    };

    const handleResize = useCallback(
        (event) => {
            if (!draggingRef.current) return;
            const delta = startYRef.current - event.clientY;
            const nextHeight = Math.max(120, Math.min(600, startHeightRef.current + delta));
            setTableHeight(nextHeight);
        },
        [setTableHeight]
    );

    const stopResize = useCallback(() => {
        if (!draggingRef.current) return;
        draggingRef.current = false;
        window.removeEventListener('mousemove', handleResize);
        window.removeEventListener('mouseup', stopResize);
    }, [handleResize]);

    useEffect(() => {
        return () => {
            window.removeEventListener('mousemove', handleResize);
            window.removeEventListener('mouseup', stopResize);
        };
    }, [handleResize, stopResize]);

    return (
        <div css={s.mainContainer}>
            <div id="user_data_map_area" css={s.mapArea}>
                {/* placeholder removed */}
            </div>
            {!isTableCollapsed && (
                <div
                    css={s.resizerBar}
                    onMouseDown={startResize}
                    role="separator"
                    aria-label="속성 테이블 높이 조절"
                >
                    <div className="attribute-resizer" />
                </div>
            )}
            <div
                css={s.attributeTableArea}
                data-collapsed={isTableCollapsed}
                style={{ height: isTableCollapsed ? 60 : tableHeight }}
            >
                <div className="attribute-header">
                    <span>
                        {attributeTitle || '속성 데이터'}
                        {!attributeTitle && selectedLayerIndex != null && layers[selectedLayerIndex]?.name
                            ? ` (${layers[selectedLayerIndex].name})`
                            : ''}
                    </span>
                    <button
                        type="button"
                        className="collapse-button"
                        onClick={() => {
                            const nextCollapsed = !isTableCollapsed;
                            setIsTableCollapsed(nextCollapsed);
                            handleTableExpand?.(!nextCollapsed);
                            console.log("펼침/닫힘 버튼 클릭")
                        }}
                    >
                        {isTableCollapsed ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </button>
                </div>
                {!isTableCollapsed && (
                    <div className="attribute-body">
                        {attributeContent ? (
                            <div className="custom-attribute-content">{attributeContent}</div>
                        ) : (
                            <div className="attribute-table-wrapper">
                                <div className="attribute-table-inner">
                                    <AttributeTable geojson={attrGeojson} />
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MapArea;
